import { config as loadDotenv, parse as parseDotenv } from 'dotenv';
import { existsSync, readFileSync } from 'node:fs';
import { dirname, parse as parsePath, resolve } from 'node:path';
import { fileURLToPath } from 'node:url';
import { homedir } from 'node:os';

function decodePnpmFileLocator(modulePath: string): string[] {
  const normalized = modulePath.replace(/\\/g, '/');
  const match = normalized.match(/@file\+([^/]+?\.tgz)(?:_|\/)/);
  if (!match) return [];

  const locator = decodeURIComponent(match[1]!)
    .replace(/\+\+/g, ':/')
    .replace(/\+/g, '/');
  const tarballPath = resolve(locator);
  const packageRoot = dirname(tarballPath);
  return [
    resolve(packageRoot, '.env'),
    resolve(packageRoot, '../../.env'),
  ];
}

function uniqueExisting(paths: string[]): string[] {
  const seen = new Set<string>();
  const result: string[] = [];

  for (const path of paths) {
    const normalized = resolve(path);
    const key = normalized.toLowerCase();
    if (seen.has(key) || !existsSync(normalized)) continue;
    seen.add(key);
    result.push(normalized);
  }

  return result;
}

function ancestorEnvPaths(startDir: string): string[] {
  const paths: string[] = [];
  let current = resolve(startDir);
  const root = parsePath(current).root;

  while (true) {
    paths.push(resolve(current, '.env'));
    if (current === root) break;
    current = dirname(current);
  }

  return paths.reverse();
}

export function getEnvSearchPaths(workspaceRoot = process.cwd(), moduleUrl = import.meta.url): string[] {
  const modulePath = fileURLToPath(moduleUrl);
  const home = homedir();
  const explicit = process.env.VEXALYN_ENV_FILE?.trim();

  return uniqueExisting([
    resolve(dirname(modulePath), '../../../.env'),
    ...decodePnpmFileLocator(modulePath),
    resolve(home, '.vexalyn', '.env'),
    resolve(home, '.vexalyn-code', '.env'),
    resolve(home, '.env'),
    ...ancestorEnvPaths(workspaceRoot),
    ...(explicit ? [explicit] : []),
  ]);
}

export function loadEnvFiles(workspaceRoot = process.cwd(), moduleUrl = import.meta.url): string[] {
  const paths = getEnvSearchPaths(workspaceRoot, moduleUrl);
  for (const path of paths) {
    loadDotenv({ path, override: true });
  }
  return paths;
}

export function readMergedEnv(workspaceRoot = process.cwd(), moduleUrl = import.meta.url): NodeJS.ProcessEnv {
  const merged: NodeJS.ProcessEnv = {};
  for (const path of getEnvSearchPaths(workspaceRoot, moduleUrl)) {
    try {
      Object.assign(merged, parseDotenv(readFileSync(path)));
    } catch {}
  }
  return merged;
}

export function readMergedEnvText(workspaceRoot = process.cwd(), moduleUrl = import.meta.url): string {
  return getEnvSearchPaths(workspaceRoot, moduleUrl)
    .map((path) => {
      try {
        return readFileSync(path, 'utf8');
      } catch {
        return '';
      }
    })
    .filter(Boolean)
    .join('\n\n');
}
